import {
  checkFirstSignal,
  checkSessionSignal,
  clearLOHPViewTimestamp,
  getLOHPViewTimestamp,
  getShowAiAssistantTimestamp,
  getViewedXDPsAfterTimestamp,
  setLOHPViewTimestamp,
  setShowAiAssistantTimestamp,
} from 'bundles/front-page/utils/coachStorageUtils';
import type { Viewed } from 'bundles/search-common/types/autocomplete';

export type SignalCheckResult = {
  shouldShow: boolean;
  viewedXDPs: Viewed[];
  isFirstTimeTrigger: boolean;
};

export const handleSessionSignalCheck = (): SignalCheckResult => {
  if (!checkSessionSignal()) {
    return { shouldShow: false, viewedXDPs: [], isFirstTimeTrigger: false };
  }

  const timestamp = getShowAiAssistantTimestamp();
  if (!timestamp) {
    return { shouldShow: false, viewedXDPs: [], isFirstTimeTrigger: false };
  }

  const xdps = getViewedXDPsAfterTimestamp(timestamp);
  return { shouldShow: true, viewedXDPs: xdps, isFirstTimeTrigger: false };
};

export const handleFirstSignalCheck = (): SignalCheckResult => {
  if (!checkFirstSignal()) {
    return { shouldShow: false, viewedXDPs: [], isFirstTimeTrigger: false };
  }

  const lohpTimestamp = getLOHPViewTimestamp();
  if (!lohpTimestamp) {
    return { shouldShow: false, viewedXDPs: [], isFirstTimeTrigger: false };
  }

  const xdps = getViewedXDPsAfterTimestamp(lohpTimestamp);
  setShowAiAssistantTimestamp(lohpTimestamp);
  clearLOHPViewTimestamp();

  return { shouldShow: true, viewedXDPs: xdps, isFirstTimeTrigger: true };
};

export const handleNoSignalDetected = (): SignalCheckResult => {
  setLOHPViewTimestamp();
  return { shouldShow: false, viewedXDPs: [], isFirstTimeTrigger: false };
};

/**
 * Performs the Coach signal checks and returns the result.
 *
 * Flow:
 * 1. Session signal (returning user): If user already triggered the signal in this session
 *    - Refresh XDPs if Coach not active, otherwise keep existing XDPs
 * 2. First signal (new trigger): If LOHP visit + XDP views detected
 *    - Extract XDPs, save original timestamp, show Coach
 * 3. No signal: Update LOHP timestamp for future eligibility
 * */
export const performCoachSignalChecks = (): SignalCheckResult => {
  const sessionResult = handleSessionSignalCheck();
  if (sessionResult.shouldShow) return sessionResult;

  const firstSignalResult = handleFirstSignalCheck();
  if (firstSignalResult.shouldShow) return firstSignalResult;

  return handleNoSignalDetected();
};
